import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";
interface SubmitInterestParams {
  workedHereBefore: boolean;
  knowsSomeone: boolean;
  contactName?: string | undefined;
  contactEmail?: string | undefined;
  contactPhone?: string | undefined;
  comments?: string | undefined;
  preferredShiftTypes: string[];
  additionalNotes?: string | undefined;
  facilityId: string;
  facilityName: string;
}

const submitInterestResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

interface SubmitToGoogleScriptParams {
  params: SubmitInterestParams;
  workerId: string;
  workerEmail: string;
  workerPhone: string;
}

async function submitInterestToGoogleScript(
  submitParams: SubmitToGoogleScriptParams
): Promise<void> {
  const { params, workerId, workerEmail, workerPhone } = submitParams;

  const payload = {
    ...params,
    workerId,
    workerEmail,
    workerPhone,
    submittedAt: new Date().toISOString(),
  };

  const formData = new URLSearchParams();
  formData.append("data", JSON.stringify(payload));

  // This is scrappy way to save data to google sheet quickly.
  // We should replace it with proper backend API in future.
  const response = await fetch(
    "https://script.google.com/macros/s/AKfycbxcAL3WurRBFCLHwKXAkVB62-kNqDINpx2aKGe_a_eYpBy5PJLUqmcW00y6YikiKGY/exec",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData.toString(),
      redirect: "follow",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to submit interest. Please try again.");
  }

  const data: unknown = await response.json();
  const validated = submitInterestResponseSchema.parse(data);

  if (!validated.success) {
    throw new Error(validated.message ?? "Failed to submit interest. Please try again.");
  }
}

export function useSubmitInterest(options?: UseMutationOptions<void, Error, SubmitInterestParams>) {
  const worker = useDefinedWorker();

  return useMutation({
    mutationFn: async (params: SubmitInterestParams) => {
      const workerEmail = worker.email ?? "";
      const workerPhone = worker.phone ?? "";

      await submitInterestToGoogleScript({
        params,
        workerId: worker.userId,
        workerEmail,
        workerPhone,
      });
    },
    ...options,
  });
}
